import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { TRADE_LOG_FILTER_INPUT_CLASS } from "@/components/trade-filter-selects";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { listBacktests } from "../../../services/backtestsApi";
import { listReportStrategies, listReports } from "../../../services/reportsApi";
import type { Backtest } from "../../../types/backtests";
import type { ReportStrategyResponse } from "../../../types/reports";
import type { WorkspaceChildActionKind } from "./workspaceChildActions";
import { filterStrategiesForAttachExisting } from "./workspaceAttachExistingStrategies";

const FETCH_LIMIT = 100;

const LIST_TITLE_CLASS =
  "truncate font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)] dark:text-white";
const LIST_MUTED_CLASS =
  "truncate font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white";
const EMPTY_STATE_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[#6b7280] dark:!text-white";

export type WorkspaceAttachExistingItem = {
  id: string;
  title: string;
  subtitle?: string;
  detail?: string;
};

export type WorkspaceAttachExistingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: WorkspaceChildActionKind;
  brokerAccountCode: string;
  /** When attaching simulations under a report, only list sims for that report. */
  scopedReportId?: string | null;
  /** Entity ids already on this canvas for `kind` — excluded from the list. */
  excludeEntityIds: ReadonlySet<string> | string[];
  onAttach: (entityId: string) => void | Promise<void>;
};

function titleForKind(kind: WorkspaceChildActionKind): string {
  if (kind === "strategy") return "Add existing strategy";
  if (kind === "report") return "Add existing report";
  return "Add existing simulation";
}

function descriptionForKind(
  kind: WorkspaceChildActionKind,
  scopedReportId?: string | null
): string {
  if (kind === "strategy") {
    return "Choose a strategy that is not already on this canvas. You can add a strategy from another account — it will be linked here.";
  }
  if (kind === "report") {
    return "Choose a report for this account that is not already on this canvas. Only unlinked reports are shown.";
  }
  if (scopedReportId?.trim()) {
    return "Choose a simulation from this report that is not already on this canvas. Only unlinked simulations are shown.";
  }
  return "Choose a simulation for this account that is not already on this canvas. Only unlinked simulations are shown.";
}

function strategyItems(
  rows: ReportStrategyResponse[],
  exclude: Set<string>
): WorkspaceAttachExistingItem[] {
  return filterStrategiesForAttachExisting(rows, exclude);
}

function reportItems(
  rows: Array<{ id: string; title?: string | null; status?: string; workspace_id?: string | null }>,
  exclude: Set<string>
): WorkspaceAttachExistingItem[] {
  return rows
    .filter((row) => {
      const id = String(row.id ?? "").trim();
      if (!id || exclude.has(id)) return false;
      if (row.workspace_id) return false;
      return true;
    })
    .map((row) => ({
      id: row.id,
      title: row.title?.trim() || "Untitled report",
      subtitle: row.status ? String(row.status) : undefined,
    }));
}

function simulationItems(
  rows: Backtest[],
  exclude: Set<string>
): WorkspaceAttachExistingItem[] {
  return rows
    .filter((row) => {
      const id = String(row.id ?? "").trim();
      if (!id || exclude.has(id)) return false;
      if (row.workspace_id) return false;
      return true;
    })
    .map((row) => ({
      id: row.id,
      title: row.title?.trim() || "Untitled simulation",
      subtitle: row.status ? String(row.status) : undefined,
    }));
}

function emptyMessageForKind(options: {
  kind: WorkspaceChildActionKind;
  scopedReportId?: string | null;
  rawCount: number;
  availableCount: number;
  searchActive: boolean;
  strategyAlreadyTakenMessage?: string;
}): string {
  const { kind, scopedReportId, rawCount, availableCount, searchActive } = options;
  if (availableCount > 0 && searchActive) return "No matches for that search.";

  if (kind === "simulation" && scopedReportId?.trim()) {
    if (rawCount === 0) {
      return "You haven't run any simulations for this report yet.";
    }
    return "All simulations for this report are already linked to a workspace or on this canvas.";
  }

  if (kind === "strategy") {
    return rawCount === 0
      ? "No strategies available to add."
      : options.strategyAlreadyTakenMessage ??
        "All matching strategies are already on this canvas.";
  }
  if (kind === "report") {
    return rawCount === 0
      ? "No reports available to add."
      : "All matching reports are already linked to a workspace or on this canvas.";
  }
  return rawCount === 0
    ? "You haven't run any simulations for this account yet."
    : "All matching simulations are already linked to a workspace or on this canvas.";
}

export type WorkspaceAttachExistingPickerProps = {
  active: boolean;
  kind: WorkspaceChildActionKind;
  brokerAccountCode: string;
  scopedReportId?: string | null;
  excludeEntityIds: ReadonlySet<string> | string[];
  onAttach: (entityId: string) => void | Promise<void>;
  /** Override empty copy when every listed strategy is already linked/on canvas. */
  strategyAlreadyTakenMessage?: string;
};

export function WorkspaceAttachExistingPicker({
  active,
  kind,
  brokerAccountCode,
  scopedReportId,
  excludeEntityIds,
  onAttach,
  strategyAlreadyTakenMessage,
}: WorkspaceAttachExistingPickerProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [attachingId, setAttachingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<WorkspaceAttachExistingItem[]>([]);
  const [rawCount, setRawCount] = useState(0);

  const exclude = useMemo(() => {
    if (excludeEntityIds instanceof Set) return excludeEntityIds;
    return new Set(excludeEntityIds);
  }, [excludeEntityIds]);

  const reportScope = scopedReportId?.trim() || "";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (kind === "strategy") {
        const resp = await listReportStrategies({
          include_archived: false,
          limit: FETCH_LIMIT,
          offset: 0,
        });
        const rows = resp.strategies ?? [];
        setRawCount(rows.length);
        setItems(strategyItems(rows, exclude));
      } else if (kind === "report") {
        const resp = await listReports({
          broker_account_code: brokerAccountCode,
          limit: FETCH_LIMIT,
          offset: 0,
        });
        const rows = resp.reports ?? [];
        setRawCount(rows.length);
        setItems(reportItems(rows, exclude));
      } else {
        const resp = await listBacktests({
          broker_account_code: brokerAccountCode,
          ...(reportScope ? { report_id: reportScope } : {}),
          limit: FETCH_LIMIT,
          offset: 0,
          include_result: false,
          include_snapshot: false,
        });
        const rows = resp.backtests ?? [];
        setRawCount(rows.length);
        setItems(simulationItems(rows, exclude));
      }
    } catch (err) {
      setItems([]);
      setRawCount(0);
      setError(err instanceof Error ? err.message : "Failed to load entities");
    } finally {
      setLoading(false);
    }
  }, [brokerAccountCode, exclude, kind, reportScope]);

  useEffect(() => {
    if (!active) return;
    setQuery("");
    setAttachingId(null);
    void load();
  }, [active, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const haystack = [item.title, item.subtitle, item.detail]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, query]);

  const handleAttach = useCallback(
    async (entityId: string) => {
      setAttachingId(entityId);
      try {
        await onAttach(entityId);
      } finally {
        setAttachingId(null);
      }
    },
    [onAttach]
  );

  const emptyMessage = emptyMessageForKind({
    kind,
    scopedReportId,
    rawCount,
    availableCount: items.length,
    searchActive: Boolean(query.trim()),
    strategyAlreadyTakenMessage,
  });

  const secondaryHeader = kind === "strategy" ? "Accounts" : "Status";
  const searchPlaceholder =
    kind === "strategy"
      ? "Search strategies…"
      : kind === "report"
        ? "Search reports…"
        : "Search simulations…";
  const searchAriaLabel =
    kind === "strategy"
      ? "Search strategies"
      : kind === "report"
        ? "Search reports"
        : "Search simulations";

  const columns = useMemo<ColumnDef<WorkspaceAttachExistingItem>[]>(
    () => [
      {
        id: "name",
        accessorFn: (row) => row.title,
        header: "Name",
        cell: ({ row }) => (
          <p className={cn("max-w-[280px]", LIST_TITLE_CLASS)} title={row.original.title}>
            {row.original.title}
          </p>
        ),
        enableSorting: false,
      },
      {
        id: "secondary",
        accessorFn: (row) => row.subtitle ?? "",
        header: secondaryHeader,
        cell: ({ row }) => (
          <p
            className={cn("max-w-[220px]", LIST_MUTED_CLASS)}
            title={row.original.detail || row.original.subtitle}
          >
            {row.original.subtitle || "—"}
          </p>
        ),
        enableSorting: false,
      },
      {
        id: "actions",
        size: 72,
        maxSize: 72,
        header: () => <span className="sr-only">Add</span>,
        meta: { align: "end" },
        cell: ({ row }) => {
          const busy = attachingId === row.original.id;
          return (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                className="h-7 shrink-0 px-2.5 text-[0.75rem] normal-case tracking-normal"
                disabled={Boolean(attachingId)}
                aria-label={`Add ${row.original.title}`}
                onClick={() => void handleAttach(row.original.id)}
              >
                {busy ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                ) : (
                  <span className="truncate normal-case tracking-normal">Add</span>
                )}
              </Button>
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [attachingId, handleAttach, secondaryHeader]
  );

  return (
    <div className="flex min-w-0 flex-col gap-3" data-workspace-attach-existing-picker="">
      <Input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={searchPlaceholder}
        autoComplete="off"
        disabled={loading || Boolean(attachingId)}
        data-font="primary"
        data-workspace-attach-existing-search=""
        className={cn(
          TRADE_LOG_FILTER_INPUT_CLASS,
          "h-8 w-full shrink-0 normal-case tracking-normal"
        )}
        aria-label={searchAriaLabel}
      />

      {loading ? (
        <div
          data-workspace-attach-existing-list=""
          className={cn(
            "flex min-h-40 flex-col items-center justify-center gap-2 rounded-[6px] border border-dashed border-border px-3 py-8",
            EMPTY_STATE_CLASS
          )}
        >
          <Spinner className="size-4 text-[color:var(--brand-tropical-surf)]" aria-hidden />
          Loading…
        </div>
      ) : error ? (
        <div
          data-workspace-attach-existing-list=""
          className="rounded-[6px] border border-border px-3 py-8 text-center font-[family-name:var(--font-primary)] text-sm text-destructive dark:border-input"
        >
          {error}
        </div>
      ) : (
        <div
          data-workspace-attach-existing-list=""
          data-trade-log-trades-table=""
          className="min-h-0 max-h-72 overflow-y-auto overscroll-contain"
        >
          <DataTable
            columns={columns}
            data={filtered}
            getRowId={(row) => row.id}
            onRowClick={(row) => {
              if (attachingId) return;
              void handleAttach(row.id);
            }}
            pagination={{ pageIndex: 0, pageSize: FETCH_LIMIT }}
            showPagination={false}
            emptyMessage={emptyMessage}
            tableClassName="dark:!bg-sidebar"
          />
        </div>
      )}
    </div>
  );
}

export function WorkspaceAttachExistingDialog({
  open,
  onOpenChange,
  kind,
  brokerAccountCode,
  scopedReportId,
  excludeEntityIds,
  onAttach,
}: WorkspaceAttachExistingDialogProps): React.ReactElement {
  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange(next);
    },
    [onOpenChange]
  );

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={titleForKind(kind)}
      description={descriptionForKind(kind, scopedReportId)}
      contentClassName="sm:max-w-md"
      bodyClassName="flex flex-none flex-col gap-3 overflow-visible [scrollbar-gutter:auto]"
      contentProps={{ "data-workspace-attach-existing": "" }}
      dismissLabel="Cancel"
    >
      <WorkspaceAttachExistingPicker
        active={open}
        kind={kind}
        brokerAccountCode={brokerAccountCode}
        scopedReportId={scopedReportId}
        excludeEntityIds={excludeEntityIds}
        onAttach={async (entityId) => {
          await onAttach(entityId);
          onOpenChange(false);
        }}
      />
    </StrategyPineDetailDialog>
  );
}
